import { useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import SidebarComponent from '../stories/molecules/sidebar'
import { sidebarSideOptions } from '../stories/molecules/sidebar/constants'
import ProjectInfoPanelHeader from './components/project-info-panel-header'
import { useGetProjectDetailsQuery } from '../../react-query/project/query/use-get-project-details-query'
import { useGetProjectPinsQuery } from '../../react-query/pins/query/use-get-project-pins-query'
import { useGetTaskListQuery } from '../../react-query/tasks/query/use-get-task-list-query'
import { useGetPlanTreeQuery } from '../../react-query/plan/query/use-get-plan-tree-query'
import { SpinnerGrey } from '../stories/atoms/icons/special'
import {
  ContentContainer,
  HeaderButtons,
  LoadingContainer,
  NotFoundContainer,
  NotFoundContent,
  ProjectInfoSectionTitle,
  ProjectInfoSectionTitleContainer
} from './styled-components'
import { iconSizeOptions } from '../stories/atoms/icons/icons-sizes'
import ProjectInfoPanelInfo from './components/project-info-panel-info'
import ProjectInfoPanelPagination from './components/project-info-panel-pagination'
import ProjectInfoPanelMembers from './components/project-info-panel-members'
import { useGetMyProjectsQuery } from '../../react-query/project/query/use-get-my-projects-query'
import { useGetProjectContactsQuery } from '../../react-query/members/query/use-get-project-contacts-query'
import { useGetProjectTeamsQuery } from '../../react-query/members/query/use-get-project-teams-query'
import { useGetProjectMembersQuery } from '../../react-query/members/query/use-get-project-members-query'
import { isEmpty } from 'lodash'
import NotFoundComponent from '../stories/molecules/not-found'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  buttonSizeOptions,
  buttonTypeOptions,
  buttonVariantOptions
} from '../stories/atoms/button/constants'
import ButtonComponent from '../stories/atoms/button'
import colorsOptions from '../stories/fundamentals/colors-styles/constants'
import { CancelBold } from '../stories/atoms/icons/image-overlay'
import { Members } from '../stories/atoms/icons/menu'
import { useMeQuery } from '../../react-query/authentication/query/use-me-query'
import { useMyProjectsQueryParams } from '../../hooks/use-my-projects-query-params'
import { useProjectMenuStore } from '../../zustand/project-menu/use-project-menu-store'
import OutsideClickWrapper from '../../utils/OutsideClickWrapper'

const NotFound = ({ handleOnClose }) => {
  const { formatMessage } = useIntl()
  return (
    <NotFoundContainer>
      <HeaderButtons>
        <ButtonComponent
          buttonType={buttonTypeOptions['ICON-ONLY']}
          variant={buttonVariantOptions['IMAGE-OVERLAY']}
          size={buttonSizeOptions.SMALL}
          onClick={handleOnClose}
        >
          <CancelBold stroke={colorsOptions.greys.white} />
        </ButtonComponent>
      </HeaderButtons>
      <NotFoundContent>
        <NotFoundComponent
          header={formatMessage({ id: 'project_not_found' })}
          description={formatMessage({
            id: 'project_not_found_description'
          })}
        />
      </NotFoundContent>
    </NotFoundContainer>
  )
}

const ProjectInfoPanel = () => {
  const { projectId } = useParams()

  const history = useHistory()

  // Queries
  const { data: projectDetails, isLoading: projectIsLoading } =
    useGetProjectDetailsQuery({
      projectId
    })

  const { data: projectPins, isLoading: projectPinsIsLoading } =
    useGetProjectPinsQuery({
      projectId,
      page: 0,
      size: 1
    })

  const { data: projectTasks, isLoading: projectTasksIsLoading } =
    useGetTaskListQuery({
      projectId,
      page: 0,
      size: 1
    })

  const { data: planTreeData, isLoading: projectPlansIsLoading } =
    useGetPlanTreeQuery({
      projectId,
      params: { page: 0, size: 1 }
    })

  const projectPlans = planTreeData?.pages[0]

  const myProjectsParams = useMyProjectsQueryParams()
  const setProjectListPageNumber = useProjectMenuStore(
    (state) => state.setProjectListPageNumber
  )

  const {
    data: myProjectsData,
    isLoading: myProjectsDataIsLoading,
    isFetching: myProjectsIsFetching
  } = useGetMyProjectsQuery({ params: myProjectsParams })

  const { data: contacts, isLoading: isLoadingContacts } =
    useGetProjectContactsQuery(projectId)

  const { data: members, isLoading: isLoadingMembers } =
    useGetProjectMembersQuery(projectId)

  const { data: teams, isLoading: isLoadingTeams } =
    useGetProjectTeamsQuery(projectId)

  const { data: user } = useMeQuery()

  const userPermissions = useMemo(
    () => user?.permissions ?? [],
    [user?.permissions]
  )

  const currentProjectsPage = myProjectsData?.pages?.[0]

  const myProjectsTree = useMemo(
    () => currentProjectsPage?.content ?? [],
    [currentProjectsPage]
  )

  const emptyProject = isEmpty(projectDetails)

  const [isOpen, setIsOpen] = useState(true)

  const handleOnClose = () => {
    setIsOpen(false)
  }

  const handleExitComplete = () => {
    history.push('/project')
  }

  return (
    <OutsideClickWrapper onCloseIntent={handleOnClose} active={isOpen}>
      <SidebarComponent
        isOpen={isOpen}
        onExitComplete={handleExitComplete}
        side={sidebarSideOptions.RIGHT}
        maxWidth="400px"
        topOffset="48px"
      >
        {projectIsLoading ? (
          <LoadingContainer>
            <SpinnerGrey size={iconSizeOptions['ICON-XL']} />
          </LoadingContainer>
        ) : (
          <>
            {emptyProject ? (
              <NotFound handleOnClose={handleOnClose} />
            ) : (
              <>
                <ProjectInfoPanelHeader
                  projectDetails={projectDetails}
                  projectPlans={projectPlans}
                  projectPins={projectPins}
                  projectTasks={projectTasks}
                  projectPinsIsLoading={projectPinsIsLoading}
                  projectTasksIsLoading={projectTasksIsLoading}
                  projectPlansIsLoading={projectPlansIsLoading}
                  userPermissions={userPermissions}
                  onClose={handleOnClose}
                />
                <ContentContainer>
                  <ProjectInfoPanelInfo projectDetails={projectDetails} />
                  <ProjectInfoSectionTitleContainer>
                    <Members
                      size={iconSizeOptions['ICON-L']}
                      specialStroke={colorsOptions.greys['grey-90']}
                    />
                    <ProjectInfoSectionTitle>
                      <FormattedMessage id="personas_projectMembers" />
                    </ProjectInfoSectionTitle>
                  </ProjectInfoSectionTitleContainer>
                  <ProjectInfoPanelMembers
                    members={members && members.content}
                    teams={teams}
                    contacts={contacts}
                    isLoadingContacts={isLoadingContacts}
                    isLoadingMembers={isLoadingMembers}
                    isLoadingTeams={isLoadingTeams}
                  />
                </ContentContainer>
                <ProjectInfoPanelPagination
                  projectDetails={projectDetails}
                  projectTree={myProjectsTree}
                  isLoading={myProjectsDataIsLoading}
                  isFetching={myProjectsIsFetching}
                  pageNumber={currentProjectsPage?.number ?? 0}
                  totalElements={currentProjectsPage?.totalElements ?? 0}
                  pageSize={currentProjectsPage?.size ?? 0}
                  isFirstPage={currentProjectsPage?.first ?? true}
                  isLastPage={currentProjectsPage?.last ?? true}
                  onPageChange={setProjectListPageNumber}
                />
              </>
            )}
          </>
        )}
      </SidebarComponent>
    </OutsideClickWrapper>
  )
}

export default ProjectInfoPanel
